"use client";

import { db } from "@/lib/firebase";
import { formatFirestoreDocument } from "@/lib/firestore";
import { subscribeShared } from "@/lib/firestore-listener-registry";
import type { ChatInboxEntry, ChatInboxType } from "@/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useEffect, useMemo, useRef } from "react";
import { usePageVisibility } from "./useVisibilityPausedQuery";

const BADGE_CAP = 99;

export interface ChatInboxGroups {
  tournament: ChatInboxEntry[];
  team: ChatInboxEntry[];
  match: ChatInboxEntry[];
}

export interface UseChatInboxResult {
  entries: ChatInboxEntry[];
  groups: ChatInboxGroups;
  totalUnread: number;
  totalUnreadLabel: string;
  isLoading: boolean;
}

/**
 * Real-time hook backing the unified header chat bell + grouped dropdown.
 *
 * Listens to `chatInboxes/{userId}/entries` — a per-user denormalized read
 * model maintained by Cloud Function fan-out triggers on each chat surface
 * (match / team / tournament). Entries are grouped by `type` for the
 * dropdown sections; archived entries are filtered out.
 *
 * One onSnapshot per session — replaces the previous three-listener model
 * (useUserChats + useUserTournamentChats + per-tournament-message scans).
 */
export function useChatInbox(userId: string | undefined): UseChatInboxResult {
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();
  const queryKey = useMemo(() => ["chatInbox", userId] as const, [userId]);

  const pendingRef = useRef<{
    resolve: (entries: ChatInboxEntry[]) => void;
    reject: (error: Error) => void;
  } | null>(null);

  useEffect(() => {
    if (!isVisible || !userId) return;

    const unsubscribe = subscribeShared<ChatInboxEntry[]>(
      `chatInbox:${userId}`,
      (onData, onError) => {
        const inboxQuery = query(
          collection(db, "chatInboxes", userId, "entries"),
          where("archived", "==", false),
          orderBy("lastMessageAt", "desc"),
        );
        return onSnapshot(
          inboxQuery,
          (snapshot) => {
            const next = snapshot.docs.map((doc) =>
              formatFirestoreDocument(doc),
            ) as ChatInboxEntry[];
            onData(next);
          },
          onError,
        );
      },
      {
        onData: (next) => {
          if (pendingRef.current) {
            pendingRef.current.resolve(next);
            pendingRef.current = null;
          }
          queryClient.setQueryData(queryKey, next);
        },
        onError: (error) => {
          console.error("Error fetching chat inbox:", error);
          if (pendingRef.current) {
            pendingRef.current.reject(error);
            pendingRef.current = null;
          }
        },
      },
    );

    return () => {
      unsubscribe();
      if (pendingRef.current) {
        const cached =
          queryClient.getQueryData<ChatInboxEntry[]>(queryKey);
        pendingRef.current.resolve(cached ?? []);
        pendingRef.current = null;
      }
    };
  }, [userId, queryClient, queryKey, isVisible]);

  const { data: entries = [], isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      new Promise<ChatInboxEntry[]>((resolve, reject) => {
        if (!userId) {
          resolve([]);
          return;
        }
        const cached = queryClient.getQueryData<ChatInboxEntry[]>(queryKey);
        if (cached !== undefined) {
          resolve(cached);
          return;
        }
        const timeoutId = setTimeout(() => {
          if (pendingRef.current) {
            pendingRef.current.resolve([]);
            pendingRef.current = null;
          }
        }, 10_000);
        pendingRef.current = {
          resolve: (next) => {
            clearTimeout(timeoutId);
            resolve(next);
          },
          reject: (error) => {
            clearTimeout(timeoutId);
            reject(error);
          },
        };
      }),
    enabled: !!userId,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 5,
  });

  return useMemo(() => {
    const groups: ChatInboxGroups = {
      tournament: [],
      team: [],
      match: [],
    };
    let totalUnread = 0;
    for (const entry of entries) {
      if (entry.archived) continue;
      const bucket = bucketFor(entry.type);
      if (!bucket) continue;
      groups[bucket].push(entry);
      totalUnread += entry.unreadCount ?? 0;
    }
    // Sort each group: unread first, then most recent activity.
    const sortGroup = (a: ChatInboxEntry, b: ChatInboxEntry): number => {
      const aHas = (a.unreadCount ?? 0) > 0;
      const bHas = (b.unreadCount ?? 0) > 0;
      if (aHas !== bHas) return bHas ? 1 : -1;
      const aT = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bT = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return bT - aT;
    };
    groups.tournament.sort(sortGroup);
    groups.team.sort(sortGroup);
    groups.match.sort(sortGroup);

    return {
      entries,
      groups,
      totalUnread,
      totalUnreadLabel:
        totalUnread > BADGE_CAP ? `${BADGE_CAP}+` : String(totalUnread),
      isLoading,
    };
  }, [entries, isLoading]);
}

function bucketFor(type: ChatInboxType): keyof ChatInboxGroups | null {
  if (type === "tournament" || type === "team" || type === "match") {
    return type;
  }
  return null;
}
